/**
 * Loads the MultiSafepay Payment Components SDK (CSS + JS) on demand and returns the global
 * `MultiSafepay` constructor. The SDK is injected once per document and cached; concurrent callers
 * share the same in-flight promise.
 *
 * Docs: https://docs.multisafepay.com/docs/payment-components (User guide § 1. Add the HTML elements)
 */

export type MultiSafepayEnv = 'test' | 'live';

// The SDK attaches a `MultiSafepay` constructor to window when components.js loads.
type MultiSafepayConstructor = new (config: {
  env: MultiSafepayEnv;
  order: unknown;
  apiToken: string;
  recurring?: unknown;
}) => MultiSafepayInstance;

export interface MultiSafepayInstance {
  init: (
    type: 'payment' | 'dropin' | 'redirection',
    options: Record<string, unknown>
  ) => void;
  getPaymentData: () => { gateway: string; payload: string; tokenize?: boolean };
  getOrderData?: () => unknown;
  getErrors: () => unknown;
  hasErrors: () => boolean;
  setState?: (state: unknown) => void;
  getState?: () => unknown;
  /** Paints the Bancontact / iDEAL QR into the drop-in. Needs the created order. */
  setQR?: (orderData?: unknown) => boolean;
  destroy?: () => void;
}

const SDK_BASE: Record<MultiSafepayEnv, string> = {
  test: 'https://testpay.multisafepay.com/sdk/components/v2',
  live: 'https://pay.multisafepay.com/sdk/components/v2',
};

const CSS_ID = 'msp-components-css';
const JS_ID = 'msp-components-js';

let loadPromise: Promise<MultiSafepayConstructor> | null = null;
let loadedEnv: MultiSafepayEnv | null = null;

function injectCss(env: MultiSafepayEnv): void {
  if (typeof document === 'undefined') return;
  let link = document.getElementById(CSS_ID) as HTMLLinkElement | null;
  const href = `${SDK_BASE[env]}/components.css`;
  if (!link) {
    link = document.createElement('link');
    link.id = CSS_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

/**
 * Ensure the SDK for `env` is loaded and return the `MultiSafepay` constructor.
 * If a different env was loaded earlier in the same page, the existing script is reused
 * (test/live share the same global) — pass the correct `env` per `new MultiSafepay({ env })`.
 */
export function loadMultiSafepaySdk(env: MultiSafepayEnv): Promise<MultiSafepayConstructor> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('MultiSafepay SDK can only load in a browser.'));
  }

  injectCss(env);

  const existing = (window as unknown as { MultiSafepay?: MultiSafepayConstructor }).MultiSafepay;
  if (existing && loadedEnv === env) {
    return Promise.resolve(existing);
  }

  if (loadPromise && loadedEnv === env) return loadPromise;

  loadedEnv = env;
  loadPromise = new Promise<MultiSafepayConstructor>((resolve, reject) => {
    const finish = () => {
      const ctor = (window as unknown as { MultiSafepay?: MultiSafepayConstructor }).MultiSafepay;
      if (ctor) resolve(ctor);
      else reject(new Error('MultiSafepay SDK loaded but global constructor is missing.'));
    };

    let script = document.getElementById(JS_ID) as HTMLScriptElement | null;
    const src = `${SDK_BASE[env]}/components.js`;

    if (script && script.src === src) {
      if ((window as unknown as { MultiSafepay?: unknown }).MultiSafepay) finish();
      else {
        script.addEventListener('load', finish, { once: true });
        script.addEventListener('error', () => reject(new Error('Failed to load MultiSafepay SDK.')), {
          once: true,
        });
      }
      return;
    }

    // Different env script already present — swap it out.
    if (script) script.remove();

    script = document.createElement('script');
    script.id = JS_ID;
    script.src = src;
    script.async = true;
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load MultiSafepay SDK.')), {
      once: true,
    });
    document.body.appendChild(script);
  });

  return loadPromise;
}
